// ============================================================
// ClientModulesTab.js
// Modules tab inside <ClientDetailPage>. Lists every Module
// with its per-Client enable state and a Switch to toggle.
//
// Toggle behaviour:
//   - ON → OFF: confirm via <ConfirmDialog> before PATCH (destructive)
//   - OFF → ON: PATCH directly, no confirm
//   - Idempotent response: no toast, no refetch
//   - Successful state change: toast.success + refetch
//   - Error: toast.error via the errorMessages translator
//
// Globally-inactive modules render with a small badge and stay
// togglable (the operator can still flip them); the backend
// rejects toggles against globally-inactive modules with
// module_not_found_or_inactive, which the dictionary translates.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-modules-list.js
//   backend/func-carehub-api/src/functions/admin-clients-modules-toggle.js
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, RotateCw } from 'lucide-react';
import Switch from '../components/Switch';
import Badge from '../components/Badge';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from '../components/Toaster';
import {
  listAdminClientModules,
  toggleAdminClientModule,
  getAdminClientAffectedModules,
} from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import StatusMessage from '../components/StatusMessage';
import './ClientModulesTab.css';

// Mirror of the backend STATELESS_MODULE_KEYS (admin-clients-modules-toggle.js).
// Stateless modules disable to 'deactivated_for_client' (no data layer);
// data-bearing modules disable to 'readonly' (data preserved, writes blocked).
// Enabling is always 'active'. The richer active/readonly/hidden picker is a
// future Lifecycle-UI sub-phase (LD-4.1); v1 keeps the on/off Switch.
const STATELESS_MODULE_KEYS = new Set([
  'carenote-quality', 'cqc-inspection-readiness', 'governance-policy-documentation',
  'care-planning-quality', 'workforce-capability-development', 'safeguarding-incident-evidence',
  'tenders-commissioning-growth', 'hr-people-management', 'oversight',
]);

// "Turn on all modules" (2026-09-30, Zuber: "a switch to turn all modules on
// to make life easy"). It enables each OFF module through the same per-module
// PATCH the Switches use, so every dependency rule and audit row is exactly
// what a hand toggle would produce. CareRota goes LAST: it is included with
// CarePlan and CarePeople, is refused until both are on, and is switched on
// by the platform when the second of them arrives.
const ENABLE_LAST = new Set(['carerota', 'assets']); // both included: switched on by their prerequisites

export function modulesToEnable(modules) {
  // telemetryOnly rows are usage-tracking keys, never something a client holds.
  // preview rows are switched on by hand, for demo clients only, until released.
  const off = (modules || []).filter((m) => !m.isActiveForClient && m.isActiveGlobally !== false && !m.telemetryOnly && !m.preview);
  return [...off.filter((m) => !ENABLE_LAST.has(m.moduleKey)), ...off.filter((m) => ENABLE_LAST.has(m.moduleKey))];
}

function disableStatusFor(moduleKey) {
  return STATELESS_MODULE_KEYS.has(moduleKey) ? 'deactivated_for_client' : 'readonly';
}

export default function ClientModulesTab({ clientId, clientName }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track which moduleKey is currently being PATCHed so we can
  // disable that row's Switch (prevents double-clicks while the
  // network round-trip is in flight).
  const [pendingKey, setPendingKey] = useState(null);

  // Disable confirm dialog state.
  const [confirmTarget, setConfirmTarget] = useState(null); // { moduleKey, moduleName } | null
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  // What ELSE this disable affects. The endpoint was built for exactly this
  // warning and had no caller until 2026-08-04 — so the cascade was computed
  // by the platform and shown to nobody, and an operator learned what broke
  // only afterwards.
  const [cascade, setCascade] = useState(null);   // { loading, affectedModules, recommendedPreconditions } | null
  const [enableAllOpen, setEnableAllOpen] = useState(false);
  const [enablingAll, setEnablingAll] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAdminClientModules(clientId);
      setModules(res.items || []);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function applyToggle(moduleKey, desiredActive) {
    setPendingKey(moduleKey);
    try {
      const lifecycleStatus = desiredActive ? 'active' : disableStatusFor(moduleKey);
      const result = await toggleAdminClientModule(clientId, moduleKey, lifecycleStatus);
      if (result.wasIdempotent) {
        // State already matched — no-op, no toast, no refetch.
        return;
      }
      toast.success(desiredActive ? 'Module enabled' : 'Module disabled');
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setPendingKey(null);
    }
  }

  function handleSwitch(mod, desiredActive) {
    if (mod.isActiveForClient && !desiredActive) {
      // Disabling — gate behind confirm, and fetch the cascade so the
      // operator sees the knock-on BEFORE confirming rather than after.
      setConfirmTarget({ moduleKey: mod.moduleKey, moduleName: mod.moduleName });
      setCascade({ loading: true, affectedModules: [], recommendedPreconditions: [] });
      getAdminClientAffectedModules(clientId, {
        moduleKey: mod.moduleKey,
        targetStatus: disableStatusFor(mod.moduleKey),
      })
        .then((res) => setCascade({
          loading: false,
          affectedModules: res.affectedModules || [],
          recommendedPreconditions: res.recommendedPreconditions || [],
        }))
        // Fail-soft: a cascade lookup must never block the disable. The
        // confirm still works, it just carries less context.
        .catch(() => setCascade(null));
      return;
    }
    applyToggle(mod.moduleKey, desiredActive);
  }

  async function handleEnableAll() {
    setEnablingAll(true);
    const failed = [];
    let enabled = 0;
    // Re-read the list between steps: enabling one module can switch another
    // on as a side effect (CareRota), which must not then be PATCHed again.
    let queue = modulesToEnable(modules);
    const tried = new Set();
    while (queue.length) {
      const mod = queue.find((m) => !tried.has(m.moduleKey));
      if (!mod) break;
      tried.add(mod.moduleKey);
      setPendingKey(mod.moduleKey);
      try {
        const result = await toggleAdminClientModule(clientId, mod.moduleKey, 'active');
        if (!result.wasIdempotent) enabled += 1;
      } catch (err) {
        failed.push(`${mod.moduleName}: ${translateError(err)}`);
      }
      try {
        const res = await listAdminClientModules(clientId);
        queue = modulesToEnable(res.items || []);
      } catch {
        queue = queue.filter((m) => m.moduleKey !== mod.moduleKey);
      }
    }
    setPendingKey(null);
    setEnablingAll(false);
    setEnableAllOpen(false);
    if (failed.length) toast.error(`Could not turn on ${failed.length} module${failed.length === 1 ? '' : 's'}. ${failed.join(' ')}`);
    else toast.success(enabled ? 'All modules are on' : 'All modules were already on');
    await fetchData();
  }

  async function handleConfirmDisable() {
    if (!confirmTarget) return;
    setConfirmSubmitting(true);
    await applyToggle(confirmTarget.moduleKey, false);
    setConfirmSubmitting(false);
    setConfirmTarget(null);
    setCascade(null);
  }

  if (loading && modules.length === 0) {
    return (
      <StatusMessage variant="loading" className="client-modules__state">
        <Loader2 size={16} className="client-modules__spinner" />
        <span>Loading modules…</span>
      </StatusMessage>
    );
  }

  if (error) {
    return (
      <StatusMessage variant="error" className="client-modules__state client-modules__state--error">
        <AlertCircle size={16} />
        <span>{error}</span>
        <button type="button" className="client-modules__retry" onClick={fetchData}>
          <RotateCw size={14} />
          Retry
        </button>
      </StatusMessage>
    );
  }

  return (
    <div className="client-modules">
      <div className="client-modules__toolbar">
        <button
          type="button"
          className="button-secondary"
          disabled={modulesToEnable(modules).length === 0 || Boolean(pendingKey) || enablingAll}
          onClick={() => setEnableAllOpen(true)}
        >
          Turn on all modules
        </button>
        {modulesToEnable(modules).length === 0 && <span className="client-modules__toolbar-note">Every module is on.</span>}
      </div>
      <ul className="client-modules__list">
        {modules.map((mod) => {
          const rowId = `module-row-${mod.moduleKey}`;
          const isPending = pendingKey === mod.moduleKey;
          return (
            <li key={mod.moduleKey} className="client-modules__row" id={rowId}>
              <div className="client-modules__info">
                <div className="client-modules__name-row">
                  <span className="client-modules__name">{mod.moduleName}</span>
                  {!mod.isActiveGlobally && (
                    <Badge variant="neutral">Globally inactive</Badge>
                  )}
                  {mod.preview && (
                    <Badge variant="info">Preview: demo clients only</Badge>
                  )}
                </div>
                <span className="client-modules__key">{mod.moduleKey}</span>
              </div>
              <Switch
                checked={mod.isActiveForClient}
                onChange={(next) => handleSwitch(mod, next)}
                disabled={isPending}
                ariaLabelledBy={rowId}
              />
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        isOpen={enableAllOpen}
        onClose={() => { if (!enablingAll) setEnableAllOpen(false); }}
        onConfirm={handleEnableAll}
        title="Turn on all modules?"
        message={(
          <>
            <p>
              This turns on {modulesToEnable(modules).length} module
              {modulesToEnable(modules).length === 1 ? '' : 's'} for {clientName}:
              {' '}{modulesToEnable(modules).map((m) => m.moduleName).join(', ')}.
            </p>
            <p>With every module on, billing charges the single all-modules price instead of each module separately.</p>
          </>
        )}
        confirmLabel="Turn on"
        isSubmitting={enablingAll}
      />

      <ConfirmDialog
        isOpen={Boolean(confirmTarget)}
        onClose={() => { if (!confirmSubmitting) { setConfirmTarget(null); setCascade(null); } }}
        onConfirm={handleConfirmDisable}
        title={confirmTarget ? `Disable ${confirmTarget.moduleName}?` : ''}
        message={
          confirmTarget ? (
            <>
              <p>
                Users in this Client will lose access to this module. {clientName} will no
                longer be able to use {confirmTarget.moduleName} until you re-enable it.
              </p>
              {cascade && cascade.loading && <p>Checking what else this affects…</p>}
              {cascade && !cascade.loading && cascade.affectedModules.length > 0 && (
                <>
                  <p><strong>This also affects:</strong></p>
                  <ul>
                    {cascade.affectedModules.map((a) => (
                      <li key={a.moduleKey}>
                        <strong>{a.moduleName}</strong> — {a.impact}
                      </li>
                    ))}
                  </ul>
                  {cascade.recommendedPreconditions.length > 0 && (
                    <>
                      <p><strong>Worth doing first:</strong></p>
                      <ul>
                        {cascade.recommendedPreconditions.map((r) => <li key={r}>{r}</li>)}
                      </ul>
                    </>
                  )}
                </>
              )}
              {cascade && !cascade.loading && cascade.affectedModules.length === 0 && (
                <p>No other active module depends on this one.</p>
              )}
            </>
          ) : ''
        }
        confirmLabel="Disable"
        isSubmitting={confirmSubmitting}
      />
    </div>
  );
}
